/**
 * Advanced Search Config Adapter for Manufacturers
 * Transforms Manufacturer Field Registry into Advanced Search field configurations
 */

import { FieldConfig } from '../../../components/TableSearch/types';
import { MANUFACTURER_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';

/**
 * Convert a field definition to Advanced Search field config
 * Handles flattening of relational fields for searching
 */
function toSearchFieldConfig(
  field: BaseFieldDefinition
): FieldConfig | FieldConfig[] | null {
  // Skip non-searchable fields
  if (!field.isSearchable) {
    return null;
  }

  // Handle relational fields - use async select for API-based relations
  if (field.isRelation && field.relationConfig) {
    const { displayField } = field.relationConfig;

    const configs: FieldConfig[] = [];

    // Get context-aware config for search context
    // Search context uses searchField (name) for value (query matching)
    const contextConfig = getContextAwareConfigFromRegistry(
      MANUFACTURER_FIELDS_REGISTRY,
      field.key as string,
      'search' // ✅ Search context uses name field
    );

    if (contextConfig) {
      // Convert to standard config for component props
      const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

      configs.push({
        key: displayField,
        label: field.label,
        dataType: 'selection', // Use selection with async config
        apiEndpoint: asyncConfig.apiEndpoint,
        searchField: asyncConfig.searchField,
        valueField: asyncConfig.valueField, // Uses name field for query matching
        labelField: asyncConfig.labelField,
      });
    } else {
      // Fallback to string search if no async config
      configs.push({
        key: displayField,
        label: field.label,
        dataType: 'string',
      });
    }

    return configs;
  }

  // Standard fields - map directly
  const dataType =
    field.dataType === 'object'
      ? 'string'
      : field.dataType === 'boolean'
        ? 'selection'
        : (field.dataType as 'string' | 'number' | 'date' | 'selection');

  return {
    key: field.key as string,
    label: field.label,
    dataType,
    options: field.selectionOptions,
  };
}

/**
 * Convert Manufacturer Field Registry to Advanced Search field configurations
 * @returns Array of FieldConfig for Advanced Search component
 */
export function toAdvancedSearchConfig(): FieldConfig[] {
  const configs: FieldConfig[] = [];

  MANUFACTURER_FIELDS_REGISTRY.forEach(field => {
    const config = toSearchFieldConfig(field);
    if (config) {
      if (Array.isArray(config)) {
        configs.push(...config);
      } else {
        configs.push(config);
      }
    }
  });

  return configs;
}

/**
 * Get searchable field keys (useful for validation)
 */
export function getSearchableFieldKeys(): string[] {
  return toAdvancedSearchConfig().map(config => config.key);
}
